/**
 * LocalePicker — custom dropdown cho định dạng số & ngày locale.
 *
 * Native <select> không support flag <img>; Unicode RIS emoji fallback thành letter pair
 * trên Windows. Custom dropdown dùng <img> từ flagcdn để render flag thật cross-platform.
 *
 * Drop direction: auto-detect — drops DOWN by default, flips UP nếu không đủ space below.
 */
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Sparkles } from 'lucide-react';
import { LOCALES } from '../utils/locales';

interface LocalePickerProps {
    value: string;
    onChange: (code: string) => void;
    autoLabel: string;
}

function countryCodeFromLocale(code: string): string {
    const parts = code.split('-');
    const country = parts[parts.length - 1];
    return country.length === 2 ? country.toLowerCase() : '';
}

const DROPDOWN_HEIGHT = 420; // estimated max height

const LocalePicker: React.FC<LocalePickerProps> = ({ value, onChange, autoLabel }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [direction, setDirection] = useState<'down' | 'up'>('down');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!isOpen) return;
        // Auto-flip direction based on available viewport space
        const rect = triggerRef.current?.getBoundingClientRect();
        if (rect) {
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            // Drop UP only when not enough below AND more space above
            if (spaceBelow < DROPDOWN_HEIGHT && spaceAbove > spaceBelow) {
                setDirection('up');
            } else {
                setDirection('down');
            }
        }
        setTimeout(() => inputRef.current?.focus(), 50);
    }, [isOpen]);

    useEffect(() => {
        const onClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
                setQuery('');
            }
        };
        if (isOpen) document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [isOpen]);

    const selected = value ? LOCALES.find(l => l.code === value) : null;
    const filtered = query
        ? LOCALES.filter(l =>
            l.code.toLowerCase().includes(query.toLowerCase()) ||
            l.label.toLowerCase().includes(query.toLowerCase()),
        )
        : LOCALES;

    const handlePick = (code: string) => {
        onChange(code);
        setIsOpen(false);
        setQuery('');
    };

    return (
        <div ref={containerRef} className="relative">
            <button
                ref={triggerRef}
                type="button"
                onClick={() => setIsOpen(o => !o)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 border rounded-lg text-sm transition-all bg-slate-50 hover:bg-white ${isOpen ? 'border-indigo-500 ring-2 ring-indigo-100' : 'border-slate-300'
                    }`}
            >
                {selected ? (
                    <span className="flex items-center gap-2 min-w-0">
                        {countryCodeFromLocale(selected.code) && (
                            <img
                                src={`https://flagcdn.com/24x18/${countryCodeFromLocale(selected.code)}.png`}
                                srcSet={`https://flagcdn.com/48x36/${countryCodeFromLocale(selected.code)}.png 2x`}
                                width="24"
                                height="18"
                                alt=""
                                loading="lazy"
                                className="rounded-sm shadow-sm shrink-0"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                        )}
                        <span className="font-medium text-slate-800 truncate">{selected.label}</span>
                        <span className="text-xs text-slate-400 shrink-0">· {selected.example}</span>
                    </span>
                ) : (
                    <span className="flex items-center gap-2 min-w-0">
                        <Sparkles className="w-4 h-4 text-indigo-500 shrink-0" />
                        <span className="font-semibold text-indigo-700 truncate">{autoLabel}</span>
                    </span>
                )}
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div
                    className={`absolute left-0 right-0 bg-white rounded-xl border border-slate-200 shadow-2xl z-50 overflow-hidden animate-in fade-in duration-150 ${direction === 'down' ? 'top-full mt-2 slide-in-from-top-1' : 'bottom-full mb-2 slide-in-from-bottom-1'
                        }`}
                    style={{ maxHeight: DROPDOWN_HEIGHT }}
                >
                    {/* Auto option — highlighted */}
                    <button
                        type="button"
                        onClick={() => handlePick('')}
                        className={`w-full flex items-center gap-2 px-3 py-2.5 border-b-2 border-indigo-100 text-left transition-colors ${value === '' ? 'bg-indigo-50' : 'bg-indigo-50/30 hover:bg-indigo-50'
                            }`}
                    >
                        <Sparkles className="w-4 h-4 text-indigo-500 shrink-0" />
                        <span className="text-sm font-bold text-indigo-700 truncate">{autoLabel}</span>
                        <span className="ml-auto text-[10px] uppercase font-bold text-indigo-500 shrink-0">Khuyến nghị</span>
                    </button>

                    {/* Search */}
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
                        <Search className="w-4 h-4 text-slate-400 shrink-0" />
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder="Tìm locale (vd: vi, English, JP...)"
                            className="flex-1 text-sm outline-none placeholder-slate-400 bg-transparent"
                        />
                        {query && (
                            <button onClick={() => { setQuery(''); inputRef.current?.focus(); }} className="text-xs text-slate-400 hover:text-slate-600">✕</button>
                        )}
                    </div>

                    {/* Locale list */}
                    <div className="overflow-y-auto py-1" style={{ maxHeight: DROPDOWN_HEIGHT - 110 }}>
                        {filtered.length === 0 ? (
                            <div className="text-center text-xs text-slate-400 py-6 italic">Không tìm thấy locale.</div>
                        ) : (
                            filtered.map(loc => {
                                const cc = countryCodeFromLocale(loc.code);
                                const active = value === loc.code;
                                return (
                                    <button
                                        type="button"
                                        key={loc.code}
                                        onClick={() => handlePick(loc.code)}
                                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${active ? 'bg-indigo-100' : 'hover:bg-slate-50'
                                            }`}
                                    >
                                        {cc ? (
                                            <img
                                                src={`https://flagcdn.com/24x18/${cc}.png`}
                                                srcSet={`https://flagcdn.com/48x36/${cc}.png 2x`}
                                                width="24"
                                                height="18"
                                                alt=""
                                                loading="lazy"
                                                className="rounded-sm shadow-sm shrink-0"
                                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                            />
                                        ) : (
                                            <span className="w-6" />
                                        )}
                                        <span className={`text-sm truncate ${active ? 'font-bold text-indigo-700' : 'text-slate-700'}`}>{loc.label}</span>
                                        <span className="text-xs text-slate-400 shrink-0">· {loc.example}</span>
                                        <span className="ml-auto text-[10px] text-slate-400 font-mono shrink-0">{loc.code}</span>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LocalePicker;
